import { localized, t } from '@weavedev/lit-i18next';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { ConfigurationDto } from '@/api-sdk';
import { iconChevronLeft } from '@/components/icons';
import {
  ConfiguratorContextType,
  configuratorGlobal,
  global,
  GlobalComponentType,
} from '@/context';
import { BREAKPOINT_SMALL_PX } from '@/settings';
import { baseStyles, buttonStyles, loadingStyles } from '@/styles';
import { ActionEvent, IntentActionEvent, IntentToEvent, Mode } from '@/types';
import { consume } from '@lit/context';

@customElement('fg-configurator-actions-top-bottom-buttons')
@localized()
export class FgConfiguratorActionsTopBottomButtons extends LitElement {
  /**
   * Configuration {@link ConfigurationDto} data from the api call, is used to validate the CTA buttons if not set
   *
   * @property {ConfigurationDto} configuration
   */
  @property({ type: Object })
  configuration?: ConfigurationDto | null;

  /**
   * Boolean to check if the quantity is validated
   *
   * @property {boolean} quantityValidated
   */
  @property({ type: Boolean })
  quantityValidated = false;

  @property({ type: Boolean })
  isMobileActionsEnabled = false;

  @property({ type: Boolean })
  unprinted = false;

  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  @consume({ context: configuratorGlobal.context, subscribe: true })
  configuratorContext!: ConfiguratorContextType;

  protected render() {
    const { layout } = this.globalContext;
    const { editing, salable, hasValidSteps } = this.configuratorContext;

    return layout !== 'wholesale' && !editing && salable
      ? html`
          <ul class="actions">
            <li>
              <button
                class="button button-primary"
                @click="${() => {
                  if (this.isMobileActionsEnabled) {
                    this.toggleConfigurator(Mode.QUOTE);
                    return;
                  }
                  if (
                    this.configuration &&
                    this.quantityValidated &&
                    hasValidSteps
                  ) {
                    this.intentTo('intent-to-add-to-quotation');
                  }
                  this.triggerValidationEvents('add-to-quotation');
                }}"
              >
                ${t('actions.addToQuotation')}
                <div class="next-icon">${iconChevronLeft}</div>
              </button>
            </li>
            ${!this.unprinted && salable
              ? html`
                  <li>
                    <button
                      class="button button-primary"
                      @click=${() => {
                        if (this.isMobileActionsEnabled) {
                          this.toggleConfigurator(Mode.DIGITAL_EXAMPLE);
                          return;
                        }

                        if (
                          this.configuration &&
                          this.quantityValidated &&
                          hasValidSteps
                        ) {
                          this.intentTo('intent-to-request-print-proof');
                        }
                        this.triggerValidationEvents('request-print-proof');
                      }}
                    >
                      ${t('actions.requestPrintProof')}
                      <div class="next-icon">${iconChevronLeft}</div>
                    </button>
                  </li>
                `
              : null}
          </ul>
        `
      : nothing;
  }
  toggleConfigurator(mode: Mode) {
    this.dispatchEvent(
      new CustomEvent('set-mode', {
        detail: {
          mode,
        },
        composed: true,
      }),
    );
    this.dispatchEvent(
      new CustomEvent<{ actionEvent: ActionEvent }>('toggle-configurator', {
        composed: true,
      }),
    );
  }

  triggerValidationEvents(actionEvent: ActionEvent) {
    this.dispatchEvent(
      new CustomEvent<{ actionEvent: ActionEvent }>(
        'trigger-validation-events',
        {
          composed: true,
          detail: {
            actionEvent: actionEvent,
          },
        },
      ),
    );
  }

  /**
   * @function intentTo
   * @param {IntentActionEvent} eventName
   * @description Dispatches an event to fg-configurator-actions.ts to handle the intent-to events
   */
  intentTo(eventName: IntentActionEvent) {
    this.dispatchEvent(
      new CustomEvent<IntentToEvent>('intent-to', {
        composed: true,
        detail: {
          eventName: eventName,
        },
      }),
    );
  }

  static styles = [
    baseStyles,
    buttonStyles,
    loadingStyles,
    css`
      :host {
        display: block;
      }

      :host > div {
        padding: 20px;
      }

      button {
        display: flex;
        justify-content: space-between;
        align-items: center;

        @media screen and (max-width: ${BREAKPOINT_SMALL_PX}px) {
          justify-content: center;
        }
      }

      .next-icon {
        width: 10px;
        height: 14px;
        transform: scaleX(-1);
        flex-shrink: 0;
        margin-left: 7px;
      }

      .actions {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        gap: 10px;
      }

      .actions .button {
        justify-content: center;
        padding: 0 15px;
        width: 100%;
        white-space: nowrap;
      }
    `,
  ];
}
